import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import {
  switchMap,
  from,
  catchError,
  of,
  Observable,
  map,
  throwError,
} from 'rxjs';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorElementsApiService {
  protected http = inject(HttpClient);
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.errorElementList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.errorElement.clear().then(() =>
                appDb.errorElement
                  .bulkAdd(
                    data.items.map((item) => ({
                      ...item,
                      isSynced: 1,
                      isDeleted: item.isDeleted ? 1 : 0,
                    }))
                  )
                  .then(() => true)
              )
            );
          }
          return from(
            appDb.errorElement
              .bulkPut(
                data.items.map((item) => ({
                  ...item,
                  isSynced: 1,
                  isDeleted: item.isDeleted ? 1 : 0,
                }))
              )
              .then(() => true)
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(data): Observable<any> {
    return this.http.post(generateURL(apiPaths.errorElement), data).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'errorElementKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('errorElementKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  update(id, data) {
    return this.http
      .put(generateURL(apiPaths.errorElementId, { id }), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'errorElementKey').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables('errorElementKey')
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  delete(id) {
    return this.http.delete(generateURL(apiPaths.errorElementId, { id })).pipe(
      switchMap((res) =>
        this.dbService.getRow(appDb.cacheKeys, 'errorElementKey').pipe(
          switchMap((cache) => {
            return this.getList(cache.hash).pipe(map(() => res));
          })
        )
      ),
      switchMap((res) =>
        this.cacheKeyService
          .checkAndUpdateTables('errorElementKey')
          .pipe(map(() => res))
      ),
      catchError((err) => throwError(() => err))
    );
  }
  bulk(errorElements) {
    return this.http.post(
      generateURL(apiPaths.errorElementBulk),
      errorElements.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }
}
